import { useState } from "react";
import { Loader2, Trash2 } from "lucide-react";

import { resolveApiErrorMessage } from "@/lib/api/error-messages";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { FormError } from "@/components/form-error";

interface DeleteConfirmButtonProps {
  entityName: string;
  name: string;
  // Navigates away on success; on failure the dialog stays open with the error.
  onConfirm: () => void;
  mutation: {
    isPending: boolean;
    error: Error | null;
    reset: () => void;
  };
}

export function DeleteConfirmButton({
  entityName,
  name,
  onConfirm,
  mutation,
}: DeleteConfirmButtonProps) {
  const [open, setOpen] = useState(false);

  const handleOpenChange = (nextOpen: boolean) => {
    if (mutation.isPending) return;
    if (nextOpen) mutation.reset();
    setOpen(nextOpen);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="text-destructive hover:bg-destructive/5 hover:text-destructive"
        >
          <Trash2 />
          Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {entityName}</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete &quot;{name}&quot;? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {mutation.error && (
          <FormError>
            {resolveApiErrorMessage(mutation.error, {
              fallback: `Failed to delete ${entityName.toLowerCase()}. Please try again.`,
            })}
          </FormError>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={mutation.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            // Radix closes the dialog on click; keep it open for the pending
            // state and a possible error.
            onClick={(event) => {
              event.preventDefault();
              onConfirm();
            }}
            disabled={mutation.isPending}
            className={buttonVariants({ variant: "destructive", size: "sm" })}
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
